import { Stack, Group, Text, Badge, Card } from '@repletoryreach/sdk-ui';
import { TextInput, TokenInput, TokenTextarea } from '@repletoryreach/sdk-ui/inputs';
import type { broadcasts } from '@repletoryreach/sdk-api';
import { useAttributeSuggestions } from '../../../../lib/attributes';
import type { StepProps } from './shared';
import { isButtonLink } from '../draft';

/**
 * Step 2 for an email broadcast (4 October 2026): the subject, the text and an
 * optional button.
 *
 * Like SMS there is no template and no number. The person writes plain text
 * (a blank line starts a new paragraph, a link is written out as it is) with
 * the same contact tokens SMS takes, and the platform lays it out, adds the
 * business's details and, for marketing, the unsubscribe link. So the preview
 * is the server's: the sender, the subject and the message as it goes out,
 * asked for once the typing has paused.
 *
 * The HTML the server renders is shown only inside a frame with every
 * permission taken away (`sandbox=""`): no script runs, no form submits, no
 * link navigates the console. It is a picture of the email, nothing more.
 */
export function EmailMessageStep({
  state, set, errors, t, preview, previewing, settled
}: StepProps & {
  preview: broadcasts.BroadcastPreview | null;
  previewing: boolean;
  /** Whether the preview was asked about the subject and text as they stand now. */
  settled: boolean;
}) {
  const suggestions = useAttributeSuggestions();
  const emailPreview = preview?.channel === 'email' ? preview : null;
  const composed = settled ? emailPreview?.email_preview ?? null : null;
  const samples = (emailPreview?.sample ?? []).filter((s) => s.email_subject || s.email_text);
  // A link the server would refuse is said as soon as it is typed, not only on Next: Next stays
  // disabled meanwhile, and a disabled button with no reason beside it is a dead end.
  const url = state.emailButtonUrl.trim();
  const urlError = errors.emailButtonUrl ?? (url && !isButtonLink(url) ? t('broadcasts.email.button.urlInvalid') : undefined);

  return (
    <div className="rr-bc-new__compose">
      <Stack gap="md">
        <TokenInput
          label={t('broadcasts.email.subject.label')}
          description={t('broadcasts.email.subject.hint')}
          error={errors.emailSubject}
          tokens={suggestions}
          fallbackHint={t('attributes.fallbackHint')}
          value={state.emailSubject}
          onChange={(e) => { const next = e.currentTarget.value; set('emailSubject', next); }}
        />
        <TokenTextarea
          label={t('broadcasts.email.body.label')}
          description={state.emailKind === 'marketing' ? t('broadcasts.email.body.hintMarketing') : t('broadcasts.email.body.hint')}
          error={errors.emailBody}
          tokens={suggestions}
          fallbackHint={t('attributes.fallbackHint')}
          value={state.emailBody}
          // Read before the updater runs: React clears the event's
          // currentTarget once the handler returns.
          onChange={(e) => { const next = e.currentTarget.value; set('emailBody', next); }}
          minRows={8}
          autosize
        />
        <TextInput
          label={t('broadcasts.email.button.label')}
          description={t('broadcasts.email.button.labelHint')}
          error={errors.emailButtonLabel}
          value={state.emailButtonLabel}
          maxLength={60}
          onChange={(e) => set('emailButtonLabel', e.currentTarget.value)}
        />
        <TextInput
          label={t('broadcasts.email.button.url')}
          description={t('broadcasts.email.button.urlHint')}
          error={urlError}
          value={state.emailButtonUrl}
          placeholder="https://"
          inputMode="url"
          onChange={(e) => set('emailButtonUrl', e.currentTarget.value)}
        />
      </Stack>

      <Stack gap={8} className="rr-bc-new__preview">
        <Group gap="xs">
          <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.form.preview')}</Text>
          {(previewing || !settled) && <Badge size="xs" variant="light">{t('common.loading')}</Badge>}
        </Group>

        <div className="rr-bc-new__email" aria-live="polite" data-testid="email-preview">
          {composed ? (
            <>
              <dl className="rr-bc-new__email-head">
                <dt>{t('broadcasts.email.preview.from')}</dt>
                <dd>{composed.from}</dd>
                {composed.reply_to && (
                  <>
                    <dt>{t('broadcasts.email.preview.replyTo')}</dt>
                    <dd>{composed.reply_to}</dd>
                  </>
                )}
                <dt>{t('broadcasts.email.subject.label')}</dt>
                <dd className="rr-bc-new__email-subject">{composed.subject}</dd>
              </dl>
              {composed.html ? (
                <iframe
                  className="rr-bc-new__email-frame"
                  title={t('broadcasts.email.preview.frameTitle')}
                  sandbox=""
                  referrerPolicy="no-referrer"
                  srcDoc={composed.html}
                />
              ) : (
                <div className="rr-bc-new__email-text">{composed.text}</div>
              )}
            </>
          ) : (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('broadcasts.email.preview.empty')}</Text>
          )}
        </div>

        <Text role="caption" c="var(--rr-color-text-muted)">
          {composed ? t('broadcasts.email.preview.note') : t('broadcasts.new.message.previewNoContact')}
        </Text>

        {samples.length > 0 && settled && (
          <Card withBorder>
            <Stack gap={8}>
              <Text role="bodySmall" fw={600}>{t('broadcasts.sms.samples.title')}</Text>
              {samples.map((s, i) => (
                <Stack key={`${s.email ?? s.msisdn}-${i}`} gap={2} className="rr-bc-new__line">
                  <Text role="caption" c="var(--rr-color-text-muted)">{s.name || s.email || s.msisdn}</Text>
                  {s.email_subject && <Text role="bodySmall" fw={600} className="rr-bc-new__sms-text">{s.email_subject}</Text>}
                  {s.email_text && <Text role="bodySmall" className="rr-bc-new__sms-text">{s.email_text}</Text>}
                </Stack>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    </div>
  );
}
